import React from 'react';
import { Monitor, Minimize2, Maximize2, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useUIDensity, DensityMode } from '@/hooks/useUIDensity';

const DENSITY_OPTIONS: Array<{
  value: DensityMode;
  label: string;
  description: string;
  icon: React.ElementType;
}> = [
  {
    value: 'auto',
    label: 'Automático',
    description: 'Detecta según tu pantalla',
    icon: Monitor,
  },
  {
    value: 'compact',
    label: 'Compacto',
    description: 'Más información visible',
    icon: Minimize2,
  },
  {
    value: 'comfortable',
    label: 'Cómodo',
    description: 'Balance óptimo',
    icon: Square,
  },
  {
    value: 'spacious',
    label: 'Espacioso',
    description: 'Más espacio entre elementos',
    icon: Maximize2,
  },
];

export function UIDensityToggle() {
  const { mode, setMode, actualMode } = useUIDensity();

  const currentOption = DENSITY_OPTIONS.find((opt) => opt.value === mode);
  const CurrentIcon = currentOption?.icon || Monitor;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="icon" 
          className="h-9 w-9"
          title="Densidad de la interfaz"
        >
          <CurrentIcon className="h-4 w-4" />
          <span className="sr-only">Cambiar densidad de UI</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Densidad de UI</span>
          {mode === 'auto' && (
            <span className="text-xs font-normal text-muted-foreground">
              ({actualMode})
            </span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {DENSITY_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isActive = mode === option.value;
          
          return (
            <DropdownMenuItem
              key={option.value}
              onClick={() => setMode(option.value)}
              className={isActive ? 'bg-accent' : ''}
            >
              <div className="flex items-start space-x-3 w-full">
                <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{option.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {option.description}
                  </div>
                </div>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
